import React from 'react';
import { Table, Badge, Card, CardBody, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaCalendarAlt, FaPhone, FaUserTie } from 'react-icons/fa';
import './DashboardStyles.css';

interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  managerName: string;
  managerContactNum: string | number;
  ageCategory: string;
  createdAt: string;
  playersCount: number;
}

interface RecentTeamsTableProps {
  teams: Team[];
  loading?: boolean;
}

const RecentTeamsTable: React.FC<RecentTeamsTableProps> = ({ teams, loading = false }) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatContactNumber = (contact: string | number) => {
    if (!contact) return 'N/A';
    const contactStr = contact.toString();
    return contactStr.length >= 10 ? contactStr : 'N/A';
  };

  const handleTeamClick = (teamId: string) => {
    navigate(`/teams/${teamId}`);
  };

  const handleViewAllTeams = () => {
    navigate('/all-clubs');
  };

  if (loading) {
    return (
      <Card className="h-100">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Recent Teams</h5>
          </div>
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <Card className="h-100">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Recent Teams</h5>
          </div>
          <div className="text-center py-4">
            <p className="text-muted mb-0">No recent teams found</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Recent Teams</h5>
          <Button 
            variant="link" 
            className="text-decoration-none p-0"
            onClick={handleViewAllTeams}
          >
            View all <i className="ph-arrow-right ms-1"></i>
          </Button>
        </div>
        
        <div className="table-responsive global-table-responsive">
          <Table className="global-table">
            <thead>
              <tr>
                <th scope="col">Team Name</th>
                <th scope="col">Short Name</th>
                <th scope="col">Manager</th>
                <th scope="col">Contact</th>
                <th scope="col">Age Category</th>
                <th scope="col">Players</th>
                <th scope="col">Created</th>
              </tr>
            </thead>
            <tbody>
              {teams.length > 0 ? (
                teams.slice(0, 5).map((team) => (
                  <tr key={team._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm me-3">
                          {team.logo ? (
                            <img
                              src={team.logo}
                              alt={team.name}
                              className="avatar-title rounded-circle"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="avatar-title bg-primary-subtle rounded-circle">
                              <i className="ph-users text-primary"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <h6 className="mb-0">{team.name}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {team.shortName}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {team.managerName}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {formatContactNumber(team.managerContactNum)}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {team.ageCategory}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {team.playersCount}
                      </span>
                    </td>
                    <td>
                      <span className="text-body-secondary">
                        {formatDate(team.createdAt)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <i className="ph-users text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-muted mt-2 mb-0">No recent teams</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default RecentTeamsTable; 